import { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const SCHOOL_POSITION: L.LatLngTuple = [15.5737, 108.4742]
const DEFAULT_ZOOM = 16

type SchoolMapProps = {
  title?: string
  address?: string
  latitude?: number
  longitude?: number
  zoom?: number
  className?: string
}

export function SchoolMap({
  title = 'Trường THCS Nguyễn Văn Linh',
  address = '123 Đường Lê Lợi, P. Tân Thạnh, TP. Tam Kỳ, Quảng Nam',
  latitude,
  longitude,
  zoom = DEFAULT_ZOOM,
  className = '',
}: SchoolMapProps) {
  const mapNode = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const mapZoom = Number.isFinite(zoom) ? zoom : DEFAULT_ZOOM
  const position = useMemo<L.LatLngTuple>(() => [
    Number.isFinite(latitude) ? latitude! : SCHOOL_POSITION[0],
    Number.isFinite(longitude) ? longitude! : SCHOOL_POSITION[1],
  ], [latitude, longitude])

  useEffect(() => {
    if (!mapNode.current || mapRef.current) return undefined

    const map = L.map(mapNode.current, {
      center: position,
      zoom: mapZoom,
      scrollWheelZoom: false,
      attributionControl: false,
    })

    L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      attribution: 'Map data © Google',
    }).addTo(map)

    L.control.attribution({ prefix: false }).addTo(map)

    const popup = document.createElement('div')
    const name = document.createElement('strong')
    name.textContent = title
    const detail = document.createElement('span')
    detail.textContent = address
    popup.append(name, document.createElement('br'), detail)

    const markerIcon = L.divIcon({
      className: 'school-map__marker',
      html: '<span></span>',
      iconSize: [32, 32],
      iconAnchor: [16, 31],
      popupAnchor: [0, -30],
    })

    L.marker(position, { icon: markerIcon })
      .addTo(map)
      .bindPopup(popup)

    mapRef.current = map

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 150)
    return () => {
      window.clearTimeout(resizeTimer)
      map.remove()
      mapRef.current = null
    }
  }, [address, mapZoom, position, title])

  return <div className={`school-map ${className}`} ref={mapNode} aria-label={`Bản đồ ${title}`} />
}
